// NEW FILE: frontend/src/pages/ChangeVehicleVIN.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { readFormId, withForm } from '../utils/wizardForm';
import HeaderLogged from '../components/HeaderLogged';
import Footer from '../components/Footer';
import IrsMaintenanceBanner from "./IrsMaintenanceBanner";
export default function ChangeVehicleVIN() {
  const { id: companyId } = useParams();
  const navigate = useNavigate();

  // Контекст поправки — из адреса (его кладёт BeginAmendedReturn)
  const [searchParams]     = useSearchParams();
  const submissionId       = searchParams.get('sid');
  const token              = localStorage.getItem('token');
  const prevSubmissionDate = searchParams.get('date');
  const prevFormId         = readFormId(searchParams);

  const [availableVins, setAvailableVins] = useState([]); // [{id, vin, gross_vehicle_label}, ...]
  const [oldVin, setOldVin] = useState('');
  const [newVin, setNewVin] = useState('');
  const [changes, setChanges] = useState([]); // [{old, new}]
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // загрузка VIN из предыдущей подачи
  useEffect(() => {
    const load = async () => {
      try {
        if (!submissionId) {
          setError('Previous submission not selected.');
          setLoading(false);
          return;
        }
        const res = await fetch(`/api/submissions/${submissionId}/vehicles/`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) throw new Error('Failed to load VIN list');
        const data = await res.json();
        setAvailableVins(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        setError('Unable to load vehicles for the previous submission.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [submissionId, token]);

  // оставить только те OLD VIN, которых ещё нет в списке изменений
  const remainingOldVins = useMemo(() => {
    const alreadyPicked = new Set(changes.map(c => c.old));
    return availableVins.filter(v => !alreadyPicked.has(v.vin));
  }, [availableVins, changes]);

  // простая валидация VIN — 17 символов A-H J-N P R-Z 0-9 (без I,O,Q)
  const normalizeVin = (val) => (val || '').toUpperCase().replace(/\s+/g, '');
  const vinRegex = /^[A-HJ-NPR-Z0-9]{17}$/;

  const validatePair = (o, n) => {
    if (!o) return 'Choose the old VIN.';
    if (!n) return 'Enter the new VIN.';
    if (!vinRegex.test(n)) return 'New VIN must be 17 characters (A–Z, 0–9), no I/O/Q.';
    if (o === n) return 'New VIN must be different from the old VIN.';
    if (changes.some(c => c.new === n)) return 'This new VIN is already in the list.';
    return '';
  };

  const handleAdd = () => {
    const ov = oldVin;
    const nv = normalizeVin(newVin);
    const err = validatePair(ov, nv);
    if (err) {
      setError(err);
      return;
    }
    setChanges(prev => [...prev, { old: ov, new: nv }]);
    setOldVin('');
    setNewVin('');
    setError('');
  };

  const handleRemove = (idx) => {
    setChanges(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    if (!prevFormId || !submissionId || !prevSubmissionDate) {
      setError('Missing context: previous form or submission not found.');
      return;
    }
    if (!changes.length) {
      setError('Add at least one VIN change.');
      return;
    }
    setSaving(true);
    setError('');

    const payload = {
      form2290: parseInt(prevFormId, 10),
      prev_submission_id: submissionId,
      change_type: 'VIN',
      prev_submission_date: prevSubmissionDate,
      amendment_month: "01",
      changes: changes.map(c => `${c.old}:${c.new}`), // ← требуемый формат ["OLD:NEW", ...]
    };

    try {
      const res = await fetch('/api/submission-detail/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const t = await res.text().catch(() => '');
        let msg = 'Failed to save VIN changes';
        try { msg = JSON.stringify(JSON.parse(t)); } catch { msg = t || msg; }
        setError(`Server error: ${msg}`);
        return;
      }
      navigate(withForm(`/company/${companyId}/amended-payment-and-submission`, prevFormId, { type: 'VIN' }));
    } catch (e) {
      console.error(e);
      setError(`Unable to save VIN changes: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-[#E4F2FF]">
        <p className="text-gray-700 text-lg">Loading VINs…</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-[#E4F2FF]">
      <HeaderLogged />
      <main className="flex-1 py-6 px-4">
        <div className="max-w-3xl mx-auto bg-white shadow-md p-6 rounded-lg">
          <h1 className="text-2xl font-semibold text-[#0F2C5E] mb-4">
            Change VIN (VIN Correction)
          </h1>

          {/* Добавление пары OLD→NEW */}
          <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 mb-6">
            {/* Old VIN (select) */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium mb-1">Old VIN</label>
              <select
                value={oldVin}
                onChange={e => setOldVin(e.target.value)}
                className="w-full border p-2 focus:outline-none focus:ring-2 focus:ring-[#0F2C5E]"
              >
                <option value="">Select Old VIN</option>
                {remainingOldVins.map(v => (
                  <option key={v.id} value={v.vin}>
                    {v.vin}
                  </option>
                ))}
              </select>
            </div>

            {/* New VIN (input) */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium mb-1">New VIN</label>
              <input
                type="text"
                value={newVin}
                onChange={e => setNewVin(normalizeVin(e.target.value))}
                maxLength={17}
                placeholder="Enter 17-character VIN"
                className="w-full border p-2 focus:outline-none focus:ring-2 focus:ring-[#0F2C5E]"
              />
              <p className="text-xs text-gray-500 mt-1">A–Z, 0–9, 17 chars (no I, O, Q)</p>
            </div>

            {/* Add button */}
            <div className="sm:col-span-1 flex items-end">
              <button
                onClick={handleAdd}
                className="w-full bg-[#0F2C5E] text-white py-2 rounded hover:bg-[#0F2C5E]/80 transition"
              >
                Add
              </button>
            </div>
          </div>

          {/* Ошибки */}
          {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

          {/* Таблица добавленных пар */}
          {changes.length > 0 ? (
            <div className="overflow-x-auto mb-6">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="text-left px-4 py-2">Old VIN</th>
                    <th className="text-left px-4 py-2">New VIN</th>
                    <th className="px-4 py-2 w-24"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {changes.map((c, idx) => (
                    <tr key={`${c.old}-${c.new}-${idx}`}>
                      <td className="px-4 py-2 font-mono">{c.old}</td>
                      <td className="px-4 py-2 font-mono">{c.new}</td>
                      <td className="px-4 py-2 text-right">
                        <button
                          onClick={() => handleRemove(idx)}
                          className="text-red-600 hover:underline text-sm"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 mb-6">No VIN changes added yet.</p>
          )}

          {/* Действия */}
          <div className="flex justify-between">
            <button
              onClick={() => navigate(-1)}
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 transition"
            >
              Back
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !changes.length}
              className={`px-4 py-2 rounded text-white transition ${
                !changes.length || saving
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-[#0F2C5E] hover:bg-[#0F2C5E]/80'
              }`}
            >
              {saving ? 'Saving…' : 'Next'}
            </button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
